import { Box, Paper } from '@mantine/core';
import React from 'react';

import { Drawer } from 'src/design/molecules/Drawer';

import classes from './BrowserLayout.module.css';

export interface IBrowserLayoutProps {
  sidebar: React.ReactNode;
  aside?: React.ReactNode;
  asideOpen?: boolean;
  onAsideClose?: () => void;
  sidebarWidth?: number;
  asideWidth?: number;
  children: React.ReactNode;
  className?: string;
  sidebarOpened?: boolean;
  onSidebarClose?: () => void;
  sidebarTitle?: string;
}

/** @registry Three-pane bordered browser surface (fixed sidebar, fluid content, optional aside, which Escape closes through onAsideClose) with independently scrolling panes; use for file/folder browser pages instead of a resizable split. The sidebar hides itself once the surface is too narrow to hold all three panes, and the same sidebar node is reachable from a left drawer driven by sidebarOpened/onSidebarClose. */
export const BrowserLayout: React.FC<IBrowserLayoutProps> = ({
  sidebar,
  aside,
  asideOpen = false,
  onAsideClose,
  sidebarWidth = 260,
  asideWidth = 380,
  children,
  className,
  sidebarOpened = false,
  onSidebarClose,
  sidebarTitle = 'Folders',
}) => {
  const showAside = asideOpen && !!aside;

  React.useEffect(() => {
    if (!showAside || !onAsideClose) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      // An open modal/menu owns Escape: closing the aside underneath it as well
      // would dismiss two layers with one key press.
      if (document.querySelector('[role="dialog"]')) return;
      onAsideClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showAside, onAsideClose]);

  return (
    <>
      <Paper
        withBorder
        radius="md"
        display="flex"
        p={0}
        data-aside-open={showAside ? 'true' : 'false'}
        className={[classes.surface, className].filter(Boolean).join(' ')}
      >
        <Box
          data-pane="sidebar"
          className={`${classes.pane} ${classes.sidebar}`}
          w={sidebarWidth}
        >
          {sidebar}
        </Box>
        <Box
          data-pane="content"
          className={`${classes.pane} ${classes.content}`}
        >
          {children}
        </Box>
        {showAside && (
          <Box
            data-pane="aside"
            className={`${classes.pane} ${classes.aside}`}
            w={asideWidth}
          >
            {aside}
          </Box>
        )}
      </Paper>

      <Drawer
        opened={sidebarOpened}
        onClose={() => onSidebarClose?.()}
        position="left"
        size={280}
        title={sidebarTitle}
        closeButtonProps={{ 'aria-label': `Close ${sidebarTitle}` }}
      >
        <Box data-pane="sidebar-drawer">{sidebar}</Box>
      </Drawer>
    </>
  );
};
